import React, { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { pluck, uniq } from 'ramda';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { CostCenterDetail, PlanUnit, PlanUnitLink } from '../types';

import {
  linkPlanUnitFormSchema,
  linkPlanUnitFormDefault
} from './utils/linkPlanUnitFormSchema';

import {
  ADMIN_SETTINGS_BULK_ADD_CLUSTER,
  ADMIN_SETTINGS_BULK_UPDATE_EMPLOYER,
  ADMIN_SETTINGS_BULK_UPDATE_POOLS
} from 'RestAPIs/endpoints';
import { useCreateResource } from 'RestAPIs/useCreateResource';
import { useUpdateResource } from 'RestAPIs/useUpdateResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';

import { ControlledToggleButtonGroup } from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { EntityDropDown } from './EntityDropdown';
import { PoolDropdown } from './PoolDropdown';
import { getOrganizationalUnits } from './useLinkDirect';
import { AxiosResponse } from 'axios';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { PRIMARY_LIGHT } from 'constants/colors';
import {
  HierarchyPlanUnit,
  getSelectedTreeHierarchy,
  isDescendant,
  isDirectDescendant
} from './utils/treeHierarchy';
import { HierarchyValue } from 'RestAPIs/hierarchy';
import { GridRowClassNameParams } from '@mui/x-data-grid-pro';

export type LinkMode =
  | 'PLAN_UNIT_ONLY'
  | 'ONLY_DIRECT_CHILDREN'
  | 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN';

interface Props {
  mode?: PlanUnitLink;
  onClose: () => void;
  details: CostCenterDetail[];
  selectedRow: PlanUnit | HierarchyValue;
  hierarchy: PlanUnit[] | HierarchyPlanUnit[] | undefined;
}

export const LinkPlanUnitForm: React.FC<Props> = ({
  mode,
  onClose,
  details,
  selectedRow,
  hierarchy
}) => {
  const [mergedRows, setMergedRows] = useState<
    PlanUnit[] | HierarchyPlanUnit[] | undefined
  >([]);
  const methods = useForm<{ linkMode: LinkMode }>({
    resolver: yupResolver(linkPlanUnitFormSchema(mode)),
    defaultValues: {
      linkMode: 'PLAN_UNIT_ONLY',
      ...linkPlanUnitFormDefault(mode, selectedRow)
    }
  });
  const queryClient = useQueryClient();

  const { mutateAsync: savePool, isLoading: isPoolLoading } = useUpdateResource(
    {
      url: ADMIN_SETTINGS_BULK_UPDATE_POOLS,
      id: ''
    }
  );
  const { mutateAsync: saveEmployer, isLoading: isEmployerLoading } =
    useUpdateResource({
      url: ADMIN_SETTINGS_BULK_UPDATE_EMPLOYER,
      id: ''
    });
  const { mutateAsync: saveCluster, isLoading: isClusterLoading } =
    useCreateResource({
      url: ADMIN_SETTINGS_BULK_ADD_CLUSTER
    });

  const isLoading = isPoolLoading || isEmployerLoading || isClusterLoading;

  const onSubmit = async (data) => {
    const units =
      methods.watch('linkMode') === 'PLAN_UNIT_ONLY'
        ? []
        : await getOrganizationalUnits(
            selectedRow.brId,
            methods.watch('linkMode')
          );

    const ids = selectedRow.brId.toString().concat(',', units.join(','));
    let save: (() => Promise<AxiosResponse<any>>) | null = null;

    if (mode === PlanUnitLink.CLUSTER) {
      const convertedData = {
        brIds: ids,
        clusterId: data.entity?.value ? parseInt(data.entity.value) : null
      };
      save = saveCluster.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.EMPLOYER) {
      const convertedData = {
        costCenterDynIds: uniq(pluck('brDynId', details)).join(','),
        employerId: data.entity?.value ? parseInt(data.entity.value) : null
      };
      save = saveEmployer.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.REQUEST_POOL) {
      const convertedData = {
        costCenterIds: ids,
        poolId: data.requestPool?.value
          ? parseInt(data.requestPool.value)
          : null
      };
      save = savePool.bind(this, convertedData);
    }
    if (mode === PlanUnitLink.EMPLOYEE_POOL) {
      const convertedData = {
        costCenterIds: ids,
        empPoolId: data.employeePool?.value
          ? parseInt(data.employeePool.value)
          : null
      };
      save = savePool.bind(this, convertedData);
    }

    save?.().then(async () => {
      onClose();
      await queryClient.invalidateQueries(['hierarchy']);
    });
  };
  const getRowClassName = (
    params: GridRowClassNameParams<HierarchyPlanUnit>
  ) => {
    const { row } = params;
    const { linkMode } = methods.watch();

    if (!row || !selectedRow) return '';

    switch (linkMode) {
      case 'PLAN_UNIT_ONLY':
        return row.brId?.toString() === selectedRow.brId?.toString()
          ? 'highlighted-row'
          : '';
      case 'ONLY_DIRECT_CHILDREN':
        return isDirectDescendant(selectedRow as HierarchyValue, row)
          ? 'highlighted-row'
          : '';
      case 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN':
        return isDescendant(selectedRow as HierarchyValue, row)
          ? 'highlighted-row'
          : '';
      default:
        return '';
    }
  };

  useEffect(() => {
    if (selectedRow && hierarchy) {
      setMergedRows(
        getSelectedTreeHierarchy(
          hierarchy as HierarchyPlanUnit[],
          selectedRow.brId.toString()
        )
      );
    }
  }, [selectedRow]);

  return (
    <MultiStepForm
      methods={methods}
      onSubmit={onSubmit}
      style={{
        overflowY: 'hidden'
      }}
    >
      <div style={{ margin: '16px 0' }}>
        {mode === PlanUnitLink.EMPLOYEE_POOL ||
        mode === PlanUnitLink.REQUEST_POOL ? (
          <PoolDropdown
            name={
              mode === PlanUnitLink.EMPLOYEE_POOL
                ? 'employeePool'
                : 'requestPool'
            }
          />
        ) : (
          <EntityDropDown mode={mode} />
        )}
      </div>
      <ControlledToggleButtonGroup
        control={methods.control}
        name="linkMode"
        color="secondary"
        label={t('which')}
        aria-label="alignment"
        inputToggleButtons={[
          {
            label: t('linkModes.planUnitOnly'),
            value: 'PLAN_UNIT_ONLY' as LinkMode,
            selected: methods.watch('linkMode') === 'PLAN_UNIT_ONLY'
          },
          {
            label: t('linkModes.onlyDirectChildren'),
            value: 'ONLY_DIRECT_CHILDREN' as LinkMode,
            selected: methods.watch('linkMode') === 'ONLY_DIRECT_CHILDREN'
          },
          {
            label: t('linkModes.planUnitAndAllOfItsChildren'),
            value: 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN' as LinkMode,
            selected:
              methods.watch('linkMode') === 'PLAN_UNIT_AND_ALL_OF_ITS_CHILDREN'
          }
        ]}
      />
      <Box height="calc(100vh - 250px)" mt={1}>
        <TableCRUDLayout
          getRowClassName={getRowClassName}
          editable={false}
          getRowId={(data) => data.brId}
          getTreeDataPath={(row) => row.hierarchy || []}
          rows={mergedRows as HierarchyPlanUnit[]}
          hideCreateAction
          sx={{
            '& .MuiDataGrid-row:not(.Mui-selected)': {
              '&.highlighted-row': {
                backgroundColor: PRIMARY_LIGHT // This color is for highlighted rows
              }
            }
          }}
          columns={[]}
          collapse={-1}
          actionsThreshold={2}
          initialState={{
            sorting: {
              sortModel: [{ field: 'code', sort: 'asc' }]
            }
          }}
        />
      </Box>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
